import axios from "axios";
import { requestAuthRecovery } from "../../utils/authRecovery";
import { debug } from "../../utils/debug";

const endpoint = import.meta.env.VITE_ENDPOINT || 'http://localhost:7071/api';

// auth is otherwise only evaluated once at mount, so a tab left open past token expiry never
// recovers; 419 is the API's Authentication Timeout. requestAuthRecovery no-ops outside an iframe,
// which keeps the admin MSAL flow out of this.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 419) {
            // path only — the query string carries the opaque token, and this lands in a console
            let path = error?.config?.url ?? '?';
            try { path = new URL(path, endpoint).pathname; } catch { /* keep it as-is */ }
            debug('http', `${status} on ${error?.config?.method?.toUpperCase() ?? '?'} ${path}`);
        }
        if (status === 419) requestAuthRecovery(null, 'session-expired');
        return Promise.reject(error);
    },
);

export const get = (path: string, payload?: any, viewAs?: string, headers?: any) => {
    let params = '';
    if (Object.keys(payload ?? {}).length > 0) {
        params = '?';
        Object.keys(payload).forEach((key) => {
            if (payload[key] !== null && payload[key] !== undefined) params += `${key}=${payload[key]}&`;
        });
        params = params.substring(0, params.length - 1);
    }
    return axios.get(`${endpoint}${path}${params}`, {
        headers: {
            'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
            ...import.meta.env.VITE_BOB_AUTH && { 'BobAuth': import.meta.env.VITE_BOB_AUTH },
            ...(headers ?? {}),
        },
    });
};

export const post = (path: string, payload: any, viewAs?: string, headers?: any) => axios.post(`${endpoint}${path}`, payload, {
    headers: {
        'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
        ...(headers ?? {}),
    },
});

export const postMulti = (path: string, data: FormData, headers?: any) => fetch(
    `${endpoint}${path}`,
    {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
            'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
            ...headers,
        },
        body: data,
    },
);

export const patch = (path: string, payload: any, viewAs?: string, headers?: any) => axios.patch(`${endpoint}${path}`, payload, {
    headers: {
        'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
        ...(headers ?? {}),
    },
});

export const put = (path: string, payload: any, viewAs?: string, headers?: any) => axios.put(`${endpoint}${path}`, payload, {
    headers: {
        'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
        ...(headers ?? {}),
    },
});

export const del = (path: string, _: any, viewAs?: string, headers?: any) => axios.delete(`${endpoint}${path}`, {
    headers: {
        'Ocp-Apim-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
        ...(headers ?? {}),
    },
});

const request = (dispatch: any, type: string, payload: any) => {

    if (payload.onSuccess) delete payload.onSuccess;

    dispatch({
        type,
        payload,
    });
};

export default request;
